import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { crmApi } from "./api";
import { splitNoteTitle } from "./types";
import type { Lead } from "./types";
import {
  Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem,
  SelectTrigger as _SelectTrigger, SelectValue as _SelectValue,
} from "../../components/ui/select";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

export interface EditingNote {
  id: string;
  leadId: string;
  notes: string;
}

/** Create/edit note modal — same box Frappe opens when a card is clicked. */
export function NoteEditModal({ fixedLeadId, leads, note, onClose, onSaved }: {
  /** Lead the note belongs to. Null = show a lead picker (global Notes page). */
  fixedLeadId: string | null;
  leads: Lead[];
  /** Null = create mode. */
  note: EditingNote | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, body] = note ? splitNoteTitle(note.notes) : ["", ""];
  const [leadId, setLeadId] = useState(note?.leadId ?? fixedLeadId ?? "");
  const [titleDraft, setTitleDraft] = useState(title);
  const [bodyDraft, setBodyDraft] = useState(body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (busy) return;
    const t = titleDraft.trim();
    const b = bodyDraft.trim();
    if (!t && !b) { setError("Write a title or some content."); return; }
    if (!leadId) { setError("Choose a lead."); return; }
    setBusy(true); setError("");
    try {
      const notes = t ? (b ? `${t}\n\n${b}` : t) : b;
      if (note) {
        await crmApi.editInteraction(note.leadId, note.id, { notes });
      } else {
        await crmApi.log(leadId, { id: crypto.randomUUID(), kind: "note", notes });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save note.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={note ? "Note" : "Create note"}
      subtitle={note ? undefined : "Capture context on any lead."}
      onClose={() => !busy && onClose()}
      width="max-w-lg"
      footer={
        <>
          <button disabled={busy} onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Cancel</button>
          <button disabled={busy} onClick={() => void save()} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/85 disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {!fixedLeadId && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink-2">Lead</label>
            <Select value={leadId} onValueChange={(v: string) => { setLeadId(v); setError(""); }} disabled={!!note}>
              <SelectTrigger className="w-full border-line bg-transparent px-3 py-2 text-sm text-ink">
                <SelectValue placeholder="Choose a lead" />
              </SelectTrigger>
              <SelectContent>
                {leads.map((l: Lead) => (
                  <SelectItem key={l.id} value={l.id} className="focus:bg-soft focus:text-ink">
                    {[l.name, l.phone].filter(Boolean).join(" · ") || l.contactId || l.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-ink-2">Title</label>
          <input value={titleDraft} onChange={e => setTitleDraft(e.target.value)} placeholder="e.g. Pricing Discussion" maxLength={120} className="w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm font-semibold text-ink outline-none focus:border-brand" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-ink-2">Content</label>
          <textarea value={bodyDraft} onChange={e => { setBodyDraft(e.target.value); setError(""); }} placeholder="What should the team know?" rows={6} maxLength={10000} className="w-full resize-none rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
        </div>
        {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
      </div>
    </Modal>
  );
}
